/**
 * Veloza Membership — the single implementation of the Membership Price button.
 *
 *   Membership Price  ([data-veloza-member], rendered by snippets/buy-buttons.liquid)
 *     → adds Veloza Membership (only if it is not in the cart yet) + the selected
 *       variant in ONE /cart/add.js request, then redirects to the cart.
 *   Guest Price       (the theme's normal submit button)
 *     → handled by Dawn's <product-form>; adds only the selected variant.
 *
 * Loaded from buy-buttons.liquid on every page that has a product form; the guard
 * below makes sure the listeners are bound once, even when quick-add modals inject
 * the snippet again.
 */
(function () {
  if (window.VelozaMembership) return;

  const routes = window.routes || {};
  const cartUrl = routes.cart_url || '/cart';
  const cartAddUrl = routes.cart_add_url || '/cart/add';

  function getProductForm(button) {
    return button.closest('product-form');
  }

  function showError(button, message) {
    const productForm = getProductForm(button);
    const wrapper = productForm && productForm.querySelector('.product-form__error-message-wrapper');
    if (!wrapper) {
      window.alert(message);
      return;
    }
    wrapper.querySelector('.product-form__error-message').textContent = message;
    wrapper.toggleAttribute('hidden', !message);
  }

  function setLoading(button, loading) {
    button.classList.toggle('loading', loading);
    if (loading) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');
  }

  async function addWithMembership(button) {
    const form = document.getElementById(button.dataset.formId);
    const guestButton = form && form.querySelector('[data-veloza-guest]');
    if (!form || button.getAttribute('aria-disabled') === 'true') return;
    if (button.disabled || (guestButton && guestButton.disabled)) return;

    // FormData also picks up the quantity input, which sits outside the form (form="…").
    const formData = new FormData(form);
    const variantId = Number(formData.get('id'));
    const quantity = parseInt(formData.get('quantity'), 10) || 1;
    const membershipVariantId = Number(button.dataset.membershipVariantId);

    if (!variantId) {
      showError(button, (window.variantStrings && window.variantStrings.unavailable) || 'Please choose an option.');
      return;
    }

    showError(button, '');
    setLoading(button, true);

    try {
      const cart = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const hasMembership = (cart.items || []).some((item) => item.variant_id === membershipVariantId);

      const items = [];
      if (!hasMembership) items.push({ id: membershipVariantId, quantity: 1 });
      items.push({ id: variantId, quantity });

      const response = await fetch(`${cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Could not add to cart.');
      }

      window.location.href = cartUrl;
    } catch (error) {
      setLoading(button, false);
      showError(button, error.message);
    }
  }

  // Keep Membership Price / Guest Price labels, prices and disabled state in sync when
  // the customer picks another variant. product-info.js re-renders the section and
  // passes the new HTML here; Dawn itself would overwrite the Guest label with "Add to cart".
  function syncButtons({ data }) {
    if (!data || !data.html) return;
    document.querySelectorAll('product-info').forEach((productInfo) => {
      const sectionId = productInfo.dataset.originalSection || productInfo.dataset.section;
      if (sectionId !== data.sectionId) return;

      ['[data-veloza-member]', '[data-veloza-guest]'].forEach((selector) => {
        const source = data.html.querySelector(selector);
        const destination = productInfo.querySelector(selector);
        if (!source || !destination) return;
        destination.innerHTML = source.innerHTML;
        destination.toggleAttribute('disabled', source.hasAttribute('disabled'));
      });
    });
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-veloza-member]');
    if (!button) return;
    event.preventDefault();
    addWithMembership(button);
  });

  function subscribeToVariantChanges() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, syncButtons);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', subscribeToVariantChanges);
  else subscribeToVariantChanges();

  window.VelozaMembership = { addWithMembership };
})();
